import { PlusCircleOutlined } from '@ant-design/icons'
import { useRequest } from 'ahooks'
import { Button, Checkbox, Modal, Table, Tooltip, TreeSelect } from 'antd'
import type { CheckboxValueType } from 'antd/es/checkbox/Group'
import type { ColumnsType } from 'antd/es/table'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { PAGE_RESOURCE_POINTS_ENUM, PpRolePermission, ResourceType } from '@/schemas'

import { PermissionOptions } from '../permission.component'

const PAGE_RESOURCE_POINTS_ARRAY = Object.keys(PAGE_RESOURCE_POINTS_ENUM).map((title) => ({
  title,
  value: PAGE_RESOURCE_POINTS_ENUM[title],
}))

const { SHOW_PARENT } = TreeSelect

interface TreeDataProps {
  title: string
  value: string
  key: string
  children: TreeDataProps[]
}

const getPageResourcePointsTreeData = () => {
  const data: TreeDataProps[] = []
  for (let i = 0; i < PAGE_RESOURCE_POINTS_ARRAY.length; i++) {
    const { title, value } = PAGE_RESOURCE_POINTS_ARRAY[i]
    const values = value.split('_')
    let currentData = data
    let prefix = ''
    for (let j = 0; j < values.length; j++) {
      let d = currentData.find((d) => d.key === prefix + values[j])
      if (!d) {
        d = {
          key: prefix + values[j],
          value: prefix + values[j],
          title,
          children: [],
        }
        currentData.push(d)
      }
      currentData = d.children
      prefix += `${values[j]}_`
    }
  }
  return data
}

interface ChooseDataProps {
  values: string[]
  role: string
  permission: number
  resourceType: ResourceType
}

export const ResourceRolePermissionDialog: React.FC<{
  resourceType: ResourceType
  visible: boolean
  onVisibleChange: any
}> = ({ resourceType, visible, onVisibleChange }) => {
  const { t } = useTranslation()
  const [chooseResourcePointVisible, setChooseResourcePointVisible] = React.useState(false)
  const [chooseData, setChooseData] = React.useState<ChooseDataProps>()

  const { data: rolePermissions = [], refresh } = useRequest<PpRolePermission[]>(
    {
      url: '/api/role-permissions/getRolePermissionsByType',
      params: {
        resourceType,
      },
    },
    {
      refreshDeps: [resourceType],
    }
  )

  const { run: updateRolePermission } = useRequest(
    (id: number, permission: number) => ({
      url: '/api/role-permissions/updateRolePermission',
      method: 'patch',
      data: {
        id,
        permission,
      },
    }),
    {
      manual: true,
      onSuccess: () => {
        refresh()
      },
    }
  )

  const { run: deleteRolePermission } = useRequest(
    (id: number) => ({
      url: '/api/role-permissions/deleteRolePermission',
      method: 'delete',
      params: {
        id,
      },
    }),
    {
      manual: true,
      onSuccess: () => {
        refresh()
      },
    }
  )

  const { run: refreshCasbinRuleByType, loading } = useRequest(
    () => ({
      url: '/api/role-permissions/afterUpdateRolePermissions',
      method: 'post',
      data: {
        resourceType,
      },
    }),
    {
      manual: true,
      onSuccess: () => {
        refresh()
      },
    }
  )

  React.useEffect(() => {
    if (!chooseResourcePointVisible) {
      setChooseData(undefined)
    }
  }, [chooseResourcePointVisible])

  const permissionChanged = (permission: CheckboxValueType[], id: number) => {
    const newPermission = permission.reduce((p, c) => Number(p) + Number(c), 0)
    updateRolePermission(id, newPermission as number)
  }

  const roleCount = {}
  rolePermissions.forEach((v, index) => {
    if (roleCount[v.role]) {
      roleCount[v.role].value++
    } else {
      roleCount[v.role] = {
        index,
        value: 1,
      }
    }
  })

  const columns: ColumnsType<PpRolePermission> = [
    {
      title: t('role'),
      width: 200,
      dataIndex: 'role',
      key: 'role',
      onCell: (current, index) => {
        if (roleCount[current.role]) {
          return { rowSpan: roleCount[current.role].index === index ? roleCount[current.role].value : 0 }
        } else {
          return { rowSpan: 1 }
        }
      },
      render: (_, record) => {
        return (
          <span>
            <span className='mr-2'>{record.role}</span>
            <Tooltip title={t('add resource point')}>
              <PlusCircleOutlined
                className='cursor-pointer'
                onClick={() => {
                  setChooseData({
                    values: rolePermissions.filter((p) => p.role === record.role).map((p) => p.resourcePoint),
                    role: record.role,
                    permission: record.permission,
                    resourceType,
                  })
                  setChooseResourcePointVisible(true)
                }}
              />
            </Tooltip>
          </span>
        )
      },
    },
    {
      title: t('resource point'),
      dataIndex: 'resourcePoint',
      key: 'resourcePoint',
      render: (_, record) => PAGE_RESOURCE_POINTS_ARRAY.find((p) => p.value === record.resourcePoint)?.title,
    },
    {
      title: t('permission.title'),
      width: 350,
      render: (_, record) => {
        return (
          <Checkbox.Group
            options={PermissionOptions}
            value={record.permission
              .toString(2)
              .split('')
              .reverse()
              .map((p, i) => Number(p) << i)
              .reverse()}
            onChange={(p) => permissionChanged(p, record.id)}
          />
        )
      },
    },
    {
      title: t('operation'),
      width: 80,
      render: (_, record) => {
        // 该角色只剩一个资源点时，无法进行删除
        const disabled = rolePermissions.filter((d) => d.role === record.role).length <= 1
        return (
          <Button
            type='primary'
            disabled={disabled}
            danger
            size='small'
            onClick={() => deleteRolePermission(record.id)}
          >
            {t('delete')}
          </Button>
        )
      },
    },
  ]

  const hideModal = () => {
    onVisibleChange(false)
  }
  const handleCancel = () => {
    hideModal()
  }
  const handleOk = () => {
    refreshCasbinRuleByType()
  }

  return (
    <Modal
      width={'80%'}
      title={t('resource.rolePermission')}
      visible={visible}
      footer={[
        <Button key='submit' type='primary' disabled={loading} loading={loading} onClick={handleOk}>
          {t('save and refresh')}
        </Button>,
      ]}
      destroyOnClose
      onCancel={handleCancel}
      bodyStyle={{ height: '500px', overflowY: 'auto' }}
    >
      <Table columns={columns} dataSource={rolePermissions} pagination={false} />
      <PageResourcePointsDialog
        chooseData={chooseData}
        visible={chooseResourcePointVisible}
        setVisible={setChooseResourcePointVisible}
        refresh={refresh}
      />
    </Modal>
  )
}

const PageResourcePointsDialog: React.FC<{
  chooseData?: ChooseDataProps
  visible: boolean
  setVisible: any
  refresh: any
}> = ({ chooseData, visible, setVisible, refresh }) => {
  const [values, setValues] = React.useState(chooseData?.values || [])

  const { run: insertRolePermission } = useRequest(
    (role: string, permission: number, resourceType, resourcePoints) => ({
      url: '/api/role-permissions/insertRolePermission',
      method: 'post',
      data: {
        role,
        permission,
        resourceType,
        resourcePoints,
      },
    }),
    {
      manual: true,
      onSuccess: () => {
        setVisible(false)
        refresh()
      },
    }
  )

  React.useEffect(() => {
    setValues(chooseData?.values || [])
  }, [chooseData])

  const treeData = getPageResourcePointsTreeData()

  const onChange = (newValue: string[]) => {
    setValues(newValue)
  }

  const handleOk = () => {
    if (chooseData) {
      insertRolePermission(chooseData.role, chooseData.permission, chooseData.resourceType, values)
    }
  }
  const handleCancel = () => {
    setVisible(false)
  }

  const tProps = {
    treeData,
    value: values,
    onChange,
    treeDefaultExpandAll: true,
    treeCheckable: true,
    showCheckedStrategy: SHOW_PARENT,
    placeholder: 'Please select',
    style: {
      width: '100%',
    },
  }
  return (
    <Modal
      width={'50%'}
      title={chooseData?.role}
      visible={visible}
      destroyOnClose
      onOk={handleOk}
      onCancel={handleCancel}
    >
      <TreeSelect {...tProps} />
    </Modal>
  )
}
